<script setup>
const fullWidthMode = inject("fullWidthMode", false);
const halfCol = computed(() => (fullWidthMode ? undefined : 6));
import EnergyChart from './EnergyChart.vue';
import TipBadge from './TipBadge.vue';
import { transformYearlyFortuneText } from '@/composables/splitSentence';

const props = defineProps({
  userName: { type: String, default: '' },
  isMobile: { type: Boolean, default: false },
  data: { type: Object, default: () => ({}) },
});

// 데이터 접근
const 전체요약 = computed(() => props.data?.전체요약 || {});
const 분기별전략 = computed(() => 전체요약.value?.분기별전략 || {});
const 분기별현황 = computed(() => 전체요약.value?.분기별현황 || {});

// 오른쪽 컬럼 데이터
const 운세흐름 = computed(() => props.data?.운세흐름 || {});
const 강점추천 = computed(() => 운세흐름.value?.주요강점?.강점추천 || '');
const 운세분석 = computed(() => 전체요약.value?.운세분석 || '');
const 운세상세 = computed(() => 전체요약.value?.운세상세 || []);
const 행운꿀팁 = computed(() => 전체요약.value?.행운꿀팁 || '');

// 분기 키 배열
const quarterKeys = ['1분기', '2분기', '3분기', '4분기'];

// 분기별 데이터 (전략 + 현황 합침)
const 분기별데이터 = computed(() => {
  return quarterKeys.map((key) => {
    const 전략 = 분기별전략.value?.[key] || {};
    const 현황 = 분기별현황.value?.[key] || {};
    return {
      특징: 전략?.특징 || '',
      현재상황: 전략?.현재상황 || '',
      에너지타입: 전략?.에너지타입 || '',
      에너지가이드: 전략?.에너지가이드 || '',
      에너지설명: 전략?.에너지설명 || '',
      평균확률: 현황?.평균확률 || 0,
    };
  });
});

// 차트 데이터 (분기별 평균확률)
const chartData = computed(() => {
  return 분기별데이터.value.map((item) => item?.평균확률 || 0);
});

// 에너지 키워드별 색상
const keywordColors = {
  폭발: { border: '#c62828', text: '#c62828', bg: 'rgba(198,40,40,0.05)' },
  집중: { border: '#e65100', text: '#e65100', bg: 'rgba(230,81,0,0.05)' },
  전환: { border: '#9f37ca', text: '#9f37ca', bg: 'rgba(159,55,202,0.05)' },
  성장: { border: '#1976d2', text: '#1976d2', bg: 'rgba(25,118,210,0.05)' },
  공유: { border: '#fbc02d', text: '#fbc02d', bg: 'rgba(251,192,45,0.05)' },
  정돈: { border: '#d6c7a1', text: '#c2ac73', bg: 'rgba(214,199,161,0.05)' },
  회복: { border: '#4fbdba', text: '#4fbdba', bg: 'rgba(79,189,186,0.05)' },
  충전: { border: '#8bc34a', text: '#8bc34a', bg: 'rgba(139,195,74,0.05)' },
  정비: { border: '#548235', text: '#548235', bg: 'rgba(84,130,53,0.05)' },
};

// 키워드에서 색상 가져오기 (fallback: 회색)
const getKeywordColor = (keyword) => {
  return keywordColors[keyword] || { border: '#999', text: '#999', bg: 'rgba(153,153,153,0.05)' };
};

</script>

<template>
  <VRow class="full-height over-hidden">
    <!-- 왼쪽 컬럼 -->
    <VCol cols="12" :md="halfCol" class="no-pad-col mobile-first">
      <VCard class="personality-card full-height right">
        <div class="energy-wrapper" :class="{ mobile: isMobile }">
          <!-- 에너지 흐름 섹션 -->
          <section class="energy-section">
            <div class="energy-section__title">
              <span>🔋</span>
              <span>2026년 에너지 흐름</span>
            </div>
            <p class="energy-section__desc">1년의 흐름을 4개의 시기로 나누어 살펴봅니다.</p>

            <!-- 에너지 차트 -->
            <EnergyChart :data="chartData" :isMobile="isMobile" />
          </section>

          <!-- 구분선 -->
          <div class="energy-divider"></div>

          <!-- 시기별 가이드 섹션 -->
          <section class="energy-section">
            <div class="energy-section__title">
              <span>🚀</span>
              <span>시기별 에너지 활용 가이드</span>
            </div>
            <p class="energy-section__desc">
              시기마다 에너지를 어떻게 쓰느냐에 따라 한 해의 여정이 더욱 편안해 질 수 있어요.
            </p>

            <!-- 분기별 카드 -->
            <div class="quarter-cards" :class="{ mobile: isMobile }">
              <div
                v-for="(item, index) in 분기별데이터"
                :key="index"
                class="quarter-card"
                :class="{ mobile: isMobile }"
                :style="{ backgroundColor: getKeywordColor(item?.에너지타입).bg }"
              >
                <!-- 카드 헤더 -->
                <div class="quarter-card__header" :class="{ mobile: isMobile }">
                  <div class="quarter-card__row">
                    <span
                      class="quarter-card__badge"
                      :style="{ backgroundColor: getKeywordColor(item?.에너지타입).border }"
                    >
                      {{ index + 1 }}분기
                    </span>
                    <!-- PC: 뱃지 옆에 타이틀 -->
                    <span v-if="!isMobile" class="quarter-card__title">{{ item?.특징 || '' }}</span>
                  </div>
                  <!-- 모바일: 타이틀 별도 줄 -->
                  <span v-if="isMobile" class="quarter-card__title">{{ item?.특징 || '' }}</span>
                  <p class="quarter-card__subtitle">{{ item?.현재상황 }}</p>
                </div>

                <!-- 카드 바디 -->
                <div class="quarter-card__body" :class="{ mobile: isMobile }">
                  <div class="quarter-card__content">
                    <div
                      class="quarter-card__keyword"
                      :style="{
                        borderColor: getKeywordColor(item?.에너지타입).border,
                        backgroundColor: getKeywordColor(item?.에너지타입).bg,
                      }"
                    >
                      <span :style="{ color: getKeywordColor(item?.에너지타입).text }">
                        {{ item?.에너지타입 || '' }}
                      </span>
                    </div>
                    <div
                      class="quarter-card__text"
                      v-html="transformYearlyFortuneText(item?.에너지가이드)"
                    ></div>
                  </div>
                  <div class="quarter-card__tip">
                    <TipBadge :color="getKeywordColor(item?.에너지타입).border" />
                    <span class="quarter-card__tip-text">
                      {{ item?.에너지타입 }}: {{ item?.에너지설명 || '' }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </VCard>
    </VCol>

    <!-- 오른쪽 컬럼 -->
    <VCol cols="12" :md="halfCol" class="no-pad-col mobile-second">
      <VCard class="personality-card full-height">
        <div class="point-wrapper" :class="{ mobile: isMobile }">
          <!-- 여정의 핵심 포인트 섹션 -->
          <section class="point-section">
            <div class="point-section__title">
              <span>✨</span>
              <span>여정의 핵심 포인트</span>
            </div>

            <!-- 포인트 카드들 -->
            <div class="point-cards" :class="{ mobile: isMobile }">
              <!-- 카드 1: 올해 작용하는 힘의 구조 -->
              <div class="point-card" :class="{ mobile: isMobile }">
                <div class="point-card__image">
                  <img src="@manseImages/yearly-fortune/img_year_0301.png" alt="" />
                </div>
                <div class="point-card__content">
                  <p class="point-card__title">올해 작용하는 힘의 구조</p>
                  <p class="point-card__text" v-html="transformYearlyFortuneText(강점추천)"></p>
                </div>
              </div>

              <!-- 카드 2: 현실에서 참고하면 좋은 방향 -->
              <div class="point-card" :class="{ mobile: isMobile }">
                <div class="point-card__image">
                  <img src="@manseImages/yearly-fortune/img_year_0302.png" alt="" />
                </div>
                <div class="point-card__content">
                  <p class="point-card__title">현실에서 참고하면 좋은 방향</p>
                  <p class="point-card__text" v-html="transformYearlyFortuneText(운세분석)"></p>
                </div>
              </div>

              <!-- 카드 3: 생활 속에서 드러나는 변화 -->
              <div class="point-card" :class="{ mobile: isMobile }">
                <div class="point-card__image">
                  <img src="@manseImages/yearly-fortune/img_year_0303.png" alt="" />
                </div>
                <div class="point-card__content">
                  <p class="point-card__title">생활 속에서 드러나는 변화</p>
                  <p
                    class="point-card__text"
                    v-html="transformYearlyFortuneText(운세상세.join(' '))"
                  ></p>
                </div>
              </div>

              <!-- 카드 4: 여정을 위한 작은 조언 -->
              <div class="point-card" :class="{ mobile: isMobile }">
                <div class="point-card__image">
                  <img src="@manseImages/yearly-fortune/img_year_0304.png" alt="" />
                </div>
                <div class="point-card__content">
                  <p class="point-card__title">여정을 위한 작은 조언</p>
                  <p class="point-card__text" v-html="transformYearlyFortuneText(행운꿀팁)"></p>
                </div>
              </div>
            </div>
          </section>
        </div>
      </VCard>
    </VCol>
  </VRow>
</template>

<style scoped lang="scss">
.energy-wrapper {
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 24px;
  padding-bottom: 130px;
  gap: 24px;

  &.mobile {
    padding: 24px 16px;
    padding-bottom: 24px;
  }
}

.energy-section {
  display: flex;
  flex-direction: column;
  gap: 8px;

  &__title {
    display: flex;
    align-items: center;
    gap: 5px;
    color: #333;
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 700;
    line-height: 24px;
  }

  &__desc {
    color: #333;
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 400;
    line-height: 24px;
    margin: 0;
  }
}

.energy-divider {
  width: 100%;
  height: 1px;
  background: #ebebeb;
}

.quarter-cards {
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-top: 16px;

  &.mobile {
    gap: 15px;
    margin-top: 0;
    margin-left: -16px;
    margin-right: -16px;
  }
}

.quarter-card {
  border-radius: 12px;
  padding: 20px 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  &.mobile {
    border-radius: 0;
  }

  &__header {
    display: flex;
    flex-direction: column;
    gap: 4px;

    &.mobile {
      gap: 4px;
    }
  }

  &__row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
  }

  &__badge {
    padding: 2px 8px;
    border-radius: 22px;
    color: #fff;
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 700;
    line-height: 24px;
  }

  &__title {
    width: 100%;
    color: #333;
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 700;
    line-height: 1.4;
  }

  &__subtitle {
    padding-left: 65px;
    color: #333;
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 400;
    line-height: 1.4;
    margin: 0;

    .quarter-card.mobile & {
      padding-left: 0;
    }
  }

  &__body {
    background: #fff;
    border-radius: 0 8px 8px 8px;
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 25px;
  }

  &__content {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
  }

  &__keyword {
    width: 70px;
    height: 70px;
    border-radius: 100px;
    border: 1px solid;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    span {
      font-family: Pretendard;
      font-size: 18px;
      font-weight: 700;
      line-height: 24px;
    }

    .quarter-card.mobile & {
      width: 50px;
      height: 50px;

      span {
        font-size: 15px;
      }
    }
  }

  &__text {
    text-align: center;
    color: #333;
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 400;
    line-height: 1.4;

    .quarter-card.mobile & {
      font-size: 14px;
    }

    p {
      margin: 0;
    }
  }

  &__tip {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
  }

  &__tip-text {
    color: #575757;
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 500;
    line-height: 1.1;
  }
}

// 오른쪽 컬럼 - 포인트 섹션
.point-wrapper {
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 24px;
  padding-bottom: 130px;

  &.mobile {
    padding: 24px 16px;
    padding-bottom: 24px;
  }
}

.point-section {
  display: flex;
  flex-direction: column;

  &__title {
    display: flex;
    align-items: center;
    gap: 5px;
    color: #333;
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 700;
    line-height: 24px;
    margin-bottom: 15px;
  }
}

.point-cards {
  display: flex;
  flex-direction: column;
  gap: 15px;

  &.mobile {
    gap: 15px;
  }
}

.point-card {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px;
  border-radius: 12px;
  background: #f8f8f8;

  &.mobile {
    flex-direction: column;
    border-radius: 0;
    margin-left: -16px;
    margin-right: -16px;
    padding: 16px;
  }

  &__image {
    flex-shrink: 0;
    width: 97px;
    height: 110px;

    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }

    .point-card.mobile & {
      width: 117px;
      height: 96px;
    }
  }

  &__content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
    background: #fff;
    border-radius: 8px;
    padding: 15px 16px;

    .point-card.mobile & {
      width: 100%;
    }
  }

  &__title {
    color: #333;
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 700;
    line-height: 24px;
    margin: 0;
  }

  &__text {
    color: #333;
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 400;
    line-height: 24px;
    margin: 0;
  }
}
</style>
